import { useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquareWarning, X, Send } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function FeedbackButton() {
  const { user } = useAuth();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [sentiment, setSentiment] = useState("frustration");
  const [sending, setSending] = useState(false);
  if (!user) return null; // only for signed-in users

  const submit = async (e) => {
    e.preventDefault();
    if (text.trim().length < 3) return;
    setSending(true);
    try {
      await api.post("/feedback", { text, path: loc.pathname, sentiment });
      toast.success("Thanks — heard loud and clear.");
      setText(""); setOpen(false);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send"); }
    finally { setSending(false); }
  };

  return (
    <>
      <button onClick={()=>setOpen(true)}
        className="fixed bottom-24 right-4 z-40 w-12 h-12 rounded-full bg-footy-red border-2 border-white/20 shadow-[0_10px_30px_rgba(230,57,70,0.5)] flex items-center justify-center hover:bg-footy-redHover active:scale-90 transition"
        title="Tell us what frustrated you" data-testid="feedback-fab">
        <MessageSquareWarning size={20} />
      </button>
      {open && (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-footy-bg/85 backdrop-blur p-0 sm:p-6" onClick={()=>setOpen(false)} data-testid="feedback-modal">
          <form onSubmit={submit} onClick={(e)=>e.stopPropagation()}
            className="w-full max-w-md bg-footy-card border border-white/10 sm:rounded-2xl rounded-t-2xl p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] max-h-[92vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-footy-red font-bold">Your voice matters</p>
                <h2 className="font-display text-3xl uppercase mt-1">Tell us what frustrated you</h2>
              </div>
              <button type="button" onClick={()=>setOpen(false)} className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20"><X size={16} /></button>
            </div>
            <div className="flex gap-2 mb-3">
              {[
                { id: "frustration", label: "Frustration" },
                { id: "idea", label: "Idea" },
                { id: "praise", label: "Praise" },
              ].map(s => (
                <button key={s.id} type="button" onClick={()=>setSentiment(s.id)} data-testid={`feedback-sentiment-${s.id}`}
                  className={sentiment === s.id ? "footy-chip-active !py-1.5" : "footy-chip !py-1.5"}>{s.label}</button>
              ))}
            </div>
            <textarea value={text} onChange={(e)=>setText(e.target.value)} rows={5} maxLength={2000}
              placeholder="Whatever's on your mind — bugs, confusing bits, missing things, or a nice word. One tap. No filters."
              className="footy-input resize-none" data-testid="feedback-text" autoFocus />
            <div className="flex justify-between items-center mt-3 text-xs text-slate-400">
              <span>{text.length}/2000 · on {loc.pathname}</span>
              <button type="submit" disabled={sending || text.trim().length < 3}
                className="footy-btn-primary !py-2 !px-5 !text-sm" data-testid="feedback-submit">
                <Send size={14} /> {sending ? "Sending…" : "Send"}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
